import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress, Box, Typography } from "@mui/material";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { endpoints } from "../../Utils/UrlEnum";
import VideoCallRoom from "../VideoCall/VideoCallRoom";
import { Vocabulary } from "../../Utils/Vocabulary";

type JitsiJoinData = {
  token: string;
  roomName: string;
  domain: string;
};

type LessonDetailsResponse = {
  data?: {
    lessonUrl?: string;
  };
};

type VideoCallTokenResponse = {
  data?: {
    token?: string;
    room_name?: string;
    jitsi_domain?: string;
  };
};

const normalizeDomain = (value: string) => {
  return value
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "")
    .split("/")[0];
};

const sanitizeRoomName = (value: string) => {
  const room = value.split("?")[0].replace(/^\/+|\/+$/g, "");
  return decodeURIComponent(room);
};

const extractRoomInfo = (url: string) => {
  try {
    const parsed = new URL(url);
    const roomName = sanitizeRoomName(parsed.pathname);
    return {
      roomName,
      domain: normalizeDomain(parsed.host),
    };
  } catch (_error) {
    const normalizedUrl = url.replace(/^https?:\/\//, "");
    const [domain, ...roomParts] = normalizedUrl.split("/");
    return {
      roomName: sanitizeRoomName(roomParts.join("/")),
      domain: normalizeDomain(domain || ""),
    };
  }
};

export default function OnlineLessonRoomPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lessonUrl, setLessonUrl] = useState<string | null>(null);
  const [jitsiJoinData, setJitsiJoinData] = useState<JitsiJoinData | null>(null);
  const [joinError, setJoinError] = useState(false);

  const handleCallEnded = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!id) return;

    const loadLessonAndToken = async () => {
      try {
        setJoinError(false);
        const lessonRes = (await getData(
          `${endpoints.onlineLessons.main}/${id}`
        )) as LessonDetailsResponse;
        const url = lessonRes?.data?.lessonUrl;

        if (!url) {
          setJoinError(true);
          return;
        }
        setLessonUrl(url);

        const { roomName, domain } = extractRoomInfo(url);
        if (!roomName || !domain) {
          setJoinError(true);
          return;
        }

        const tokenPayload = {
          // eslint-disable-next-line camelcase
          room_name: roomName,
        };
        const tokenRes = (await postData(
          endpoints.onlineLessons.generateToken,
          tokenPayload
        )) as VideoCallTokenResponse;

        const token = tokenRes?.data?.token;
        const tokenRoomName = sanitizeRoomName(
          tokenRes?.data?.room_name || roomName
        );
        const tokenDomain = normalizeDomain(
          tokenRes?.data?.jitsi_domain || domain
        );

        if (token && tokenRoomName && tokenDomain) {
          setJitsiJoinData({
            token,
            roomName: tokenRoomName,
            domain: tokenDomain,
          });
          return;
        }

        setJoinError(true);
      } catch (_error) {
        setJoinError(true);
      }
    };

    loadLessonAndToken();
  }, [id]);

  if (!lessonUrl) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: { xs: "calc(100dvh - 120px)", md: "calc(100dvh - 100px)" },
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (jitsiJoinData) {
    const displayName =
      localStorage.getItem("fullName") ||
      localStorage.getItem("userName") ||
      "User";

    return (
      <VideoCallRoom
        domain={jitsiJoinData.domain}
        roomName={jitsiJoinData.roomName}
        token={jitsiJoinData.token}
        displayName={displayName}
        height="calc(100dvh - 100px)"
        onCallEnded={handleCallEnded}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: { xs: "calc(100dvh - 120px)", md: "calc(100dvh - 100px)" },
        minHeight: 300,
      }}
    >
      <Typography>{joinError ? Vocabulary.error : Vocabulary.noResultsFound}</Typography>
    </Box>
  );
}
